import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Mail,
  Phone,
  Calendar,
  Sparkles,
  Lock,
  LogOut,
  RefreshCcw,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Seo from "@/components/site/Seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "sol_admin_passcode";
const PAGE_SIZE = 10;

export default function AdminPage() {
  const [passcode, setPasscode] = useState(
    () => sessionStorage.getItem(STORAGE_KEY) || "",
  );
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // debounce server-side search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // reset offset when search changes
  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch]);

  const attemptLoad = useCallback(
    async (code, nextOffset = offset, nextQ = debouncedSearch) => {
      setLoading(true);
      setErrorMsg("");
      try {
        const params = { limit: PAGE_SIZE, offset: nextOffset };
        if (nextQ) params.q = nextQ;
        const res = await axios.get(`${API}/contact`, {
          headers: { "x-admin-passcode": code },
          params,
        });
        setSubmissions(res.data.items || []);
        setTotal(res.data.total || 0);
        setOffset(res.data.offset ?? nextOffset);
        setAuthed(true);
        sessionStorage.setItem(STORAGE_KEY, code);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          setAuthed(false);
          sessionStorage.removeItem(STORAGE_KEY);
          setErrorMsg("Invalid passcode.");
        } else {
          setErrorMsg("Unable to reach the server. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, offset],
  );

  // initial + on-passcode-change auto-load
  useEffect(() => {
    window.scrollTo({ top: 0 });
    document.title = "Admin · Smiles of Loudoun";
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) attemptLoad(stored, 0, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reload on search change while authed
  useEffect(() => {
    if (authed) attemptLoad(passcode, 0, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  function login(e) {
    e.preventDefault();
    attemptLoad(passcode, 0, "");
  }

  function logout() {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPasscode("");
    setSubmissions([]);
    setTotal(0);
    setOffset(0);
  }

  function goPage(newOffset) {
    if (newOffset < 0 || newOffset >= total) return;
    attemptLoad(passcode, newOffset, debouncedSearch);
  }

  if (!authed) {
    return (
      <section
        data-testid="admin-login-section"
        className="min-h-[70vh] grid place-content-center px-6 py-20"
      >
        <Seo title="Admin" description="Restricted area." path="/admin" noIndex />
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-8 md:p-10 w-full max-w-md"
        >
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[var(--primary)] grid place-content-center">
              <Lock className="w-5 h-5 text-[var(--accent)]" />
            </div>
            <div>
              <div className="font-mono-eyebrow text-[var(--text-muted)]">
                Admin Access
              </div>
              <h1 className="font-serif-display text-[28px] text-[var(--primary)] leading-tight">
                Contact submissions
              </h1>
            </div>
          </div>
          <label className="block mt-7">
            <span className="block text-xs uppercase tracking-[0.22em] text-[var(--text-muted)] mb-2">
              Passcode
            </span>
            <input
              data-testid="admin-passcode-input"
              type="password"
              required
              autoFocus
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Enter admin passcode"
              className="w-full px-4 py-3.5 bg-[var(--bg)] border border-[var(--border)] rounded-2xl text-[var(--primary)] focus:outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20"
            />
          </label>
          {errorMsg && (
            <div
              data-testid="admin-login-error"
              className="mt-4 flex items-start gap-2 text-sm bg-red-50 border border-red-200 text-red-700 rounded-xl p-3"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[var(--primary)] text-[var(--bg)] hover:bg-[var(--primary-dark)] disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Verifying…
              </>
            ) : (
              <>Unlock</>
            )}
          </button>
          <p className="mt-4 text-xs text-[var(--text-muted)] text-center">
            For staff only · Passcode never leaves this device after sign-out.
          </p>
        </form>
      </section>
    );
  }

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + submissions.length, total);

  return (
    <section
      data-testid="admin-dashboard"
      className="px-6 md:px-10 lg:px-16 py-14 md:py-20"
    >
      <Seo title="Admin" description="Restricted area." path="/admin" noIndex />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-eyebrow text-[var(--text-muted)]">
            Admin · <span data-testid="admin-total">{total}</span> submission{total === 1 ? "" : "s"}
          </div>
          <h1 className="font-serif-display text-[var(--primary)] mt-3 text-[44px] md:text-[60px] leading-[1] tracking-tight">
            Inbox.
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            data-testid="admin-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, message…"
            className="px-4 py-3 bg-[var(--surface)] border border-[var(--border)] rounded-full text-sm w-full sm:w-72 focus:outline-none focus:border-[var(--accent)]"
          />
          <button
            data-testid="admin-refresh"
            onClick={() => attemptLoad(passcode, offset, debouncedSearch)}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)]"
          >
            <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[var(--primary)] text-[var(--bg)] hover:bg-[var(--primary-dark)]"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      {submissions.length === 0 ? (
        <div
          data-testid="admin-empty"
          className="bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-12 text-center text-[var(--text-muted)]"
        >
          {total === 0 && !debouncedSearch
            ? "No contact submissions yet."
            : `No results for "${debouncedSearch}".`}
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            {submissions.map((s, i) => (
              <SubmissionCard key={s.id} idx={i} sub={s} />
            ))}
          </div>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div
              data-testid="admin-pagination"
              className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4"
            >
              <div className="text-sm text-[var(--text-muted)]">
                Showing <span data-testid="admin-page-start">{pageStart}</span>–
                <span data-testid="admin-page-end">{pageEnd}</span> of{" "}
                <span data-testid="admin-page-total">{total}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="admin-prev-page"
                  disabled={offset === 0 || loading}
                  onClick={() => goPage(Math.max(0, offset - PAGE_SIZE))}
                  className="inline-flex items-center gap-1 px-4 py-2.5 rounded-full border border-[var(--border)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <button
                  data-testid="admin-next-page"
                  disabled={offset + PAGE_SIZE >= total || loading}
                  onClick={() => goPage(offset + PAGE_SIZE)}
                  className="inline-flex items-center gap-1 px-4 py-2.5 rounded-full border border-[var(--border)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function SubmissionCard({ sub, idx }) {
  const date = new Date(sub.created_at);
  const fmt = date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return (
    <article
      data-testid={`admin-submission-${idx}`}
      className="bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-6 md:p-7"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-serif-display text-[24px] text-[var(--primary)] leading-tight">
            {sub.name}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[var(--text-muted)] mt-1">
            <a
              href={`mailto:${sub.email}`}
              className="flex items-center gap-1.5 hover:text-[var(--accent)]"
            >
              <Mail className="w-3.5 h-3.5" /> {sub.email}
            </a>
            <a
              href={`tel:${sub.phone}`}
              className="flex items-center gap-1.5 hover:text-[var(--accent)]"
            >
              <Phone className="w-3.5 h-3.5" /> {sub.phone}
            </a>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {sub.service && (
            <span className="text-[11px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full bg-[var(--accent-soft)] text-[var(--primary)]">
              <Sparkles className="inline w-3 h-3 mr-1 -mt-0.5" />
              {sub.service}
            </span>
          )}
          {sub.preferred_time && (
            <span className="text-[11px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full bg-[var(--bg)] border border-[var(--border)] text-[var(--text-muted)]">
              <Calendar className="inline w-3 h-3 mr-1 -mt-0.5" />
              {sub.preferred_time}
            </span>
          )}
          <span className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-muted)]">
            {fmt}
          </span>
        </div>
      </header>
      <p className="mt-5 text-[15px] text-[var(--text-main)]/85 leading-relaxed whitespace-pre-wrap">
        {sub.message}
      </p>
    </article>
  );
}
